import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'src/analytics/KatalLogger';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';
import { eErrorMessages } from 'src/constants/generic-constants';
import XptMessages from 'src/constants/xpt-messages';
import { ForecastGridRowData, ForecastRowDataStructured, ForecastTemplateCorpSegmentDropdowns } from 'src/models/ForecastModels';
import { PlanningCycleEntity } from 'src/models/PlanningCycleModel';
import { RootState } from 'src/store/store';
import { ForecastGridFixedFields, ForecastTemplateDataStatuses } from '../forecast-utils/ForecastGridConstants';
import {
  flattenForecastRowDataStructured,
  generateAndProcessCorpSegmentFilterValues,
  getForecastInputDataFromS3,
  getForecastTemplateHeaderInfo
} from '../forecast-utils/ForecastTemplateUtils';
import {
  setForecastDataSubmitClickCount,
  setForecastDataSubmitError,
  setForecastDataSubmitting,
  setForecastTemplateCompleteData,
  setForecastTemplateCorpSegmentDropdowns,
  setForecastTemplateDataLoading,
  setForecastTemplateDataStatus,
  setValidationStatus
} from './forecastTemplateSlice';
import { INITIAL_VALIDATION_STATUS } from '../services';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const fetchForecastTemplateCompleteData = createAsyncThunk(
  'forecastTemplate/fetchForecastTemplateCompleteData',
  async (currentPlanningCycle: PlanningCycleEntity, { getState, dispatch, rejectWithValue }) => {
    dispatch(setForecastTemplateDataLoading(true));
    logger.info(`Starting forecast template data fetch for planning cycle: ${currentPlanningCycle.scenario_year}`);

    const state = getState() as RootState;
    const { userAccessForCurrentBusinessGroup, userCostCentersForCurrentBusinessGroup, userProductLinesForCurrentBusinessGroup } =
      state.xptAccessAndAuthorizationStore!;

    let businessGroup = state.businessGroupStore.currentBusinessGroup;
    let attempts = 0;

    // Retry logic to wait for businessGroup to be defined
    while (!businessGroup && attempts < 5) {
      await delay(100); // Wait for 100ms before retrying
      businessGroup = state.businessGroupStore.currentBusinessGroup;
      attempts += 1;
      logger.debug(`Waiting for business group to be defined, attempt ${attempts}/5`);
    }

    if (!businessGroup || !userAccessForCurrentBusinessGroup) {
      const errorMsg = 'No business group selected';
      logger.error(errorMsg);
      dispatch(setForecastTemplateDataLoading(false));
      return rejectWithValue(errorMsg);
    }

    const dataClassificationId = businessGroup.data_classification.data_classification_id;
    const businessGroupShortDesc = businessGroup.data_classification.data_classification_short_description;

    if (!dataClassificationId || !currentPlanningCycle || !currentPlanningCycle.scenario_seq_id) {
      const errorMsg = 'No planning cycle selected to fetchForecastTemplateCompleteData';
      logger.error(errorMsg);
      dispatch(setForecastTemplateDataLoading(false));
      return rejectWithValue(errorMsg);
    }

    try {
      const forecastHeaderInfo = getForecastTemplateHeaderInfo(businessGroup, currentPlanningCycle);
      const optionalCorpSegmentsHeader = forecastHeaderInfo.corpSegmentOptionalFields;
      let forecastRowDataResponseFromS3: ForecastRowDataStructured[] = [];
      try {
        // Step 1: Loads the data from S3 for corresponding planning cycle
        logger.info(
          `Fetching forecast data from S3 for business group: ${businessGroupShortDesc}, planning cycle ID: ${currentPlanningCycle.scenario_seq_id}`
        );
        forecastRowDataResponseFromS3 = await getForecastInputDataFromS3(
          dataClassificationId,
          businessGroupShortDesc,
          currentPlanningCycle.scenario_seq_id
        );
      } catch (error: any) {
        if (error.message === eErrorMessages.NO_DATA_FOUND) {
          logger.error(`No forecast data found in S3 for planning cycle ID: ${currentPlanningCycle.scenario_seq_id}`);
          dispatch(setForecastDataSubmitError(XptMessages.FORECAST_FETCH_FAILED_S3_NO_DATA));
        } else {
          logger.error(`Error fetching forecast data from S3: ${error.message}`);
          dispatch(setForecastDataSubmitError(XptMessages.FORECAST_FETCH_FAILED_S3));
        }
      }

      logger.info(`Step 1: Loaded ${forecastRowDataResponseFromS3.length} raw forecast records from S3`);

      // Step 2: Flattens, adds FE metadata fields to the data
      const flattenedData: ForecastGridRowData[] = flattenForecastRowDataStructured(forecastRowDataResponseFromS3, optionalCorpSegmentsHeader);

      logger.info(`Step 2: Flattened ${flattenedData.length} forecast records with metadata fields`);

      const isBudgetOwner = userAccessForCurrentBusinessGroup.isBudgetOwner;
      const currentUserAlias = userAccessForCurrentBusinessGroup?.user_alias;

      logger.info(`User access details - Budget Owner: ${isBudgetOwner}, User: ${currentUserAlias}`);
      logger.info(
        `User has access to ${userCostCentersForCurrentBusinessGroup.length} cost centers and ${userProductLinesForCurrentBusinessGroup.length} product lines`
      );

      // Step 3: Apply User Accessible Cost Centers filter.
      // For Budget Owner's they can see only their data and their CC's
      const authorizedForecastDataForCurrentUser: ForecastGridRowData[] = flattenedData.filter((row) => {
        if (isBudgetOwner) {
          return (
            currentUserAlias === row[ForecastGridFixedFields.BudgetOwner.value] &&
            userCostCentersForCurrentBusinessGroup.includes(row[CorpSegmentNames.COST_CENTER]) &&
            userProductLinesForCurrentBusinessGroup.includes(row[CorpSegmentNames.PRODUCT_LINE])
          );
        }
        // For all others, they can see their CC's
        else {
          return (
            userCostCentersForCurrentBusinessGroup.includes(row[CorpSegmentNames.COST_CENTER]) &&
            userProductLinesForCurrentBusinessGroup.includes(row[CorpSegmentNames.PRODUCT_LINE])
          );
        }
      });

      logger.info(
        `Step 3: Filtered to ${authorizedForecastDataForCurrentUser.length} authorized records for user ${currentUserAlias} (${
          flattenedData.length - authorizedForecastDataForCurrentUser.length
        } records filtered out)`
      );

      if (isBudgetOwner) {
        const ownedByUser = flattenedData.filter((row) => currentUserAlias === row[ForecastGridFixedFields.BudgetOwner.value]).length;
        logger.info(
          `Budget owner details: ${ownedByUser} records owned by current user, ${authorizedForecastDataForCurrentUser.length} records accessible`
        );
      }

      logger.info(
        `Forecast data fetched successfully: for Business Group: ${businessGroupShortDesc} with Planning Cycle: ${
          currentPlanningCycle.scenario_year
        } (Seq ID: ${currentPlanningCycle.scenario_seq_id}). - Total records fetched from s3: ${
          flattenedData?.length || 0
        } and Authorized records for user ${userAccessForCurrentBusinessGroup?.user_alias}: ${authorizedForecastDataForCurrentUser?.length || 0}`
      );

      const corpSegmentFilters: ForecastTemplateCorpSegmentDropdowns[] = [];
      businessGroup.corp_segments
        .filter((corpSegment) => corpSegment.corp_segment_required)
        .forEach((corpSegment) => {
          const uniqueSortedValues = generateAndProcessCorpSegmentFilterValues(authorizedForecastDataForCurrentUser, corpSegment);
          const corpSegmentFilter: ForecastTemplateCorpSegmentDropdowns = {
            key: corpSegment.corp_segment_id,
            segmentKey: corpSegment.corp_segment_name_key,
            displayName: corpSegment.corp_segment_name,
            isRequired: corpSegment.corp_segment_required,
            isMultiSelect: true, // By default all Corp Segments are Multi Select
            loadingStatus: 'finished',
            fieldDropdownOptions: uniqueSortedValues
          };
          corpSegmentFilters.push(corpSegmentFilter);
        });

      dispatch(setForecastTemplateCorpSegmentDropdowns(corpSegmentFilters));
      dispatch(setForecastTemplateCompleteData(authorizedForecastDataForCurrentUser));
      dispatch(setValidationStatus(INITIAL_VALIDATION_STATUS));
      dispatch(setForecastTemplateDataLoading(false));

      logger.info(
        `Forecast data processing complete: ${corpSegmentFilters.length} corp segment filters created, ${authorizedForecastDataForCurrentUser.length} total records available to user`
      );
    } catch (error: any) {
      logger.error(XptMessages.FORECAST_FETCH_FAILED, error);
      dispatch(setForecastTemplateCorpSegmentDropdowns([]));
      dispatch(setForecastTemplateCompleteData([]));
      dispatch(setForecastTemplateDataLoading(false));
      dispatch(setValidationStatus(INITIAL_VALIDATION_STATUS));
      dispatch(setForecastDataSubmitError(XptMessages.FORECAST_FETCH_FAILED));
      return rejectWithValue(XptMessages.FORECAST_FETCH_FAILED);
    }
  }
);

export const clearForecastTemplateCompleteData = createAsyncThunk('forecastTemplate/clearForecastTemplateCompleteData', async (_, { dispatch }) => {
  logger.info('Clearing forecast template data and resetting all related states');
  dispatch(setForecastTemplateCompleteData([]));
  dispatch(setForecastTemplateCorpSegmentDropdowns([]));

  dispatch(setForecastDataSubmitError(''));
  dispatch(setForecastTemplateDataStatus({ dataStatus: ForecastTemplateDataStatuses.NotLoaded }));
  dispatch(setForecastDataSubmitClickCount(0));
  dispatch(setForecastDataSubmitting(false));
  dispatch(setForecastTemplateDataLoading(false));
  dispatch(setValidationStatus(INITIAL_VALIDATION_STATUS));
  logger.info('Forecast template data cleared successfully');
});
